import { useState } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import { useNavigate } from 'react-router'
import Text from '../text/text'
import { Icon } from '../icon/icon'
import { useTranslation } from 'react-i18next'
import { format as formatDate } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import Chip from '../chip/Chip'
import type { EventBaseDates } from '~/types/event'
import { fi, sv, enUS, da, nb } from 'react-day-picker/locale'
import { useClientLang } from '~/utils/i18n.utils'

const getLocaleFromLng = (lng: string) => {
	switch (lng) {
		case 'fi':
			return fi
		case 'sv':
			return sv
		case 'da':
			return da
		case 'nb':
			return nb
		default:
			return enUS
	}
}

export default function Calendar({
	dates,
	selectedDate,
	timeZone,
}: {
	dates: EventBaseDates[]
	selectedDate: EventBaseDates
	timeZone: string
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const lng = useClientLang().lngShort

	const availableDates = dates.map(date =>
		toZonedTime(new Date(date.dateFrom), timeZone),
	)
	const [selected, setSelected] = useState<Date>(
		toZonedTime(new Date(selectedDate.dateFrom), timeZone),
	)

	const [, setselectedTime] = useState<string | undefined>(() => {
		if (selectedDate?.dateFrom) {
			const zonedDate = toZonedTime(new Date(selectedDate.dateFrom), timeZone)
			return formatDate(zonedDate, 'HH:mm')
		}
		return undefined
	})

	const [availableTimes, setAvailableTimes] = useState<Date[]>(() => {
		return availableDates.filter(
			date => date.toDateString() === selected.toDateString(),
		)
	})

	const handleDateSelect = (date: Date | undefined) => {
		if (date) {
			setSelected(date)

			const timesForDate = dates.filter(availableDate => {
				const availableDateTime = toZonedTime(
					new Date(availableDate.dateFrom),
					timeZone,
				)
				return availableDateTime.toDateString() === date.toDateString()
			})
			setAvailableTimes(
				timesForDate.map(d => toZonedTime(new Date(d.dateFrom), timeZone)),
			)
			if (timesForDate.length > 0) {
				const firstTimeSlot = timesForDate[0]
				const firstDateTime = toZonedTime(
					new Date(firstTimeSlot.dateFrom),
					timeZone,
				)
				setselectedTime(formatDate(firstDateTime, 'HH:mm'))
				navigate(firstTimeSlot.url, {
					preventScrollReset: true,
				})
			}
		}
	}

	const handleTimeSelect = (time: string | undefined) => {
		if (time) {
			setselectedTime(time)

			const [hours, minutes] = time.split(':').map(Number)
			const selectedDateTime = toZonedTime(selected, timeZone)
			selectedDateTime.setHours(hours, minutes)

			// Find the matching time slot by comparing the dates in the target timezone
			const matchingTimeSlot = dates.find(slot => {
				const slotDateTime = toZonedTime(new Date(slot.dateFrom), timeZone)
				return slotDateTime.getTime() === selectedDateTime.getTime()
			})

			if (matchingTimeSlot) {
				navigate(matchingTimeSlot.url, {
					preventScrollReset: true,
				})
			}
		}
	}

	return (
		<div>
			<div className="flex w-full flex-row items-center gap-x-4 rounded-t-lg bg-secondary/5 px-4 py-3">
				<Icon size="md" name="calendar" />
				<Text as="h3" className="text-lg font-normal">
					{t('action_select_date_and_time')}
				</Text>
			</div>
			<div className="p-4">
				<DayPicker
					animate
					captionLayout="dropdown"
					mode="single"
					defaultMonth={toZonedTime(selectedDate.dateFrom, timeZone)}
					selected={toZonedTime(selectedDate.dateFrom, timeZone)}
					startMonth={availableDates[0]}
					endMonth={availableDates[availableDates.length - 1]}
					showOutsideDays
					weekStartsOn={1}
					locale={getLocaleFromLng(lng)}
					onSelect={handleDateSelect}
					modifiersClassNames={{
						available: 'border border-secondary',
					}}
					disabled={date => {
						// Disable dates that are not in availableDates
						return !availableDates.some(
							availableDate =>
								availableDate.toDateString() === date.toDateString(),
						)
					}}
					modifiers={{
						available: availableDates,
					}}
					classNames={{
						today: `text-secondary`,
						selected: `bg-secondary border-border text-white`,
						chevron: `fill-secondary`,
						months: 'w-full',
						month_grid: 'w-full',
						dropdown:
							'bg-white dark:bg-background border border-border rounded-md  p-1',
						caption_label: 'hidden',
					}}
				/>
				<div className="mt-4 flex flex-row gap-x-2">
					{availableTimes.map(time => (
						<button
							key={time.toISOString()}
							onClick={() => handleTimeSelect(formatDate(time, 'HH:mm'))}
						>
							<Chip
								size="md"
								color={
									formatDate(
										toZonedTime(selectedDate.dateFrom, timeZone),
										'HH:mm',
									) === formatDate(time, 'HH:mm')
										? 'secondary'
										: 'primary'
								}
								text={formatDate(time, 'HH:mm')}
							/>
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
